import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ALL_EXPERIMENTS,
  // Manual experiments - Physics
  createSimplePendulum,
  createProjectileMotion,
  createSHM,
  createOhmsLaw,
  createNewtonsLaw,
  createRCCircuit,
  createCircularMotion,
  // Manual experiments - Chemistry
  createChemicalEquilibrium,
  createReactionKinetics,
  createTitration,
  // Manual experiments - Biology
  createEnzymeKinetics,
  createPopulationDynamics,
  createDiffusion,
  // Generated experiment classes - Physics
  AtwoodMachine,
  DoubleAtwoodMachine,
  InclinedPlaneFriction,
  ElasticCollision1D,
  InelasticCollision1D,
  Collision2D,
  PulleyWithFriction,
  InclineHangingMass,
  BlockOnMovingSurface,
  BankedCurveMotion,
  UnderdampedOscillator,
  CriticallyDampedOscillator,
  OverdampedOscillator,
  ForcedOscillation,
  CoupledPendulums,
  CoupledSpringMass,
  TorsionalPendulum,
  PhysicalPendulum,
  StandingWavesString,
  SoundWaveInterference,
  DopplerMovingSource,
  DopplerMovingObserver,
  BeatFrequency,
  WaveSuperposition,
  IdealGasIsothermal,
  IdealGasAdiabatic,
  IdealGasIsobaric,
  IdealGasIsochoric,
  CarnotCycle,
  HeatConduction1D,
  SnellsLawRefraction,
  TotalInternalReflection,
  ThinConvergingLens,
  ThinDivergingLens,
  ConcaveMirror,
  ConvexMirror,
  DoubleSlitInterference,
  SingleSlitDiffraction,
  DiffractionGrating,
  MalusLawPolarization,
  CoulombsLaw,
  ElectricFieldPointCharge,
  ParallelPlateCapacitor,
  RLCircuitTransient,
  RLCSeriesResonance,
  RLCParallelResonance,
  MagneticForceWire,
  FaradayLaw,
  IdealTransformer,
  LCOscillations,
  PhotoelectricEffect,
  ComptonScattering,
  BlackbodyRadiation,
  BohrModel,
  RadioactiveDecay,
  RadioactiveDecayChain,
  // Generated experiment classes - Chemistry
  VaporPressureClausiusClapeyron,
  RaoultsLawIdealMixture,
  BoilingPointElevation,
  FreezingPointDepression,
  OsmoticPressure,
  HenrysLaw,
  PhaseDiagramWaterLike,
  ColligativeProperties,
  GalvanicCellNernst,
  ElectrolysisFaraday,
  ConcentrationCell,
  pHElectrodeCalibration,
  ConductivityTitration,
  ElectroplatingRate,
  HesssLawVerification,
  BombCalorimetry,
  EnthalpyOfNeutralization,
  EnthalpyOfSolution,
  // Generated experiment classes - Biology
  HardyWeinbergEquilibrium,
  GeneticDrift,
  NaturalSelectionDirectional,
  NaturalSelectionStabilizing,
  MutationRateEffects,
  GeneFlowBetweenPopulations,
  CompetitionLotkaVolterra,
  PredatorSaturationTypeII,
  PredatorSwitchingTypeIII,
  SIREpidemicModel,
  AgeStructuredPopulation,
  EnergyFlowTrophicLevels,
  FacilitatedDiffusion,
  ActiveTransportATP,
  SodiumPotassiumATPase,
  Chemiosmosis,
} from '@axlabs/experiments';
import type { SimulationState, ParameterValues, ExperimentConfig, SimulationEngine } from '@axlabs/simulation-core';
import { ParameterPanel } from '../components/ParameterPanel';
import { SimulationCanvas } from '../components/SimulationCanvas';
import { EquationDisplay } from '../components/EquationDisplay';
import { GraphPanel } from '../components/GraphPanel';
import styles from './ExperimentPage.module.css';

// Factory functions for manual experiments
type SimulationFactory = () => SimulationEngine;

// Class constructors for generated experiments
type ExperimentClassConstructor = new () => SimulationEngine;

// Union type for both factory functions and class constructors
type SimulationCreator = SimulationFactory | ExperimentClassConstructor;

// Map experiment slugs to their factory functions (manual) or class constructors (generated)
const SIMULATION_CREATORS: Record<string, SimulationCreator> = {
  // === MANUAL EXPERIMENTS - Physics ===
  'simple-pendulum': createSimplePendulum,
  'projectile-motion': createProjectileMotion,
  'shm-spring': createSHM,
  'ohms-law': createOhmsLaw,
  'newtons-second-law': createNewtonsLaw,
  'rc-circuit': createRCCircuit,
  'circular-motion': createCircularMotion,

  // === MANUAL EXPERIMENTS - Chemistry ===
  'chemical-equilibrium': createChemicalEquilibrium,
  'reaction-kinetics': createReactionKinetics,
  'titration': createTitration,

  // === MANUAL EXPERIMENTS - Biology ===
  'enzyme-kinetics': createEnzymeKinetics,
  'population-dynamics': createPopulationDynamics,
  'diffusion-osmosis': createDiffusion,

  // === GENERATED EXPERIMENTS - Physics Mechanics ===
  'atwood-machine': AtwoodMachine,
  'double-atwood-machine': DoubleAtwoodMachine,
  'inclined-plane-friction': InclinedPlaneFriction,
  'elastic-collision-1d': ElasticCollision1D,
  'inelastic-collision-1d': InelasticCollision1D,
  'collision-2d': Collision2D,
  'pulley-with-friction': PulleyWithFriction,
  'incline-hanging-mass': InclineHangingMass,
  'block-on-moving-surface': BlockOnMovingSurface,
  'banked-curve-motion': BankedCurveMotion,

  // === GENERATED EXPERIMENTS - Physics Oscillations ===
  'underdamped-oscillator': UnderdampedOscillator,
  'critically-damped-oscillator': CriticallyDampedOscillator,
  'overdamped-oscillator': OverdampedOscillator,
  'forced-oscillation': ForcedOscillation,
  'coupled-pendulums': CoupledPendulums,
  'coupled-spring-mass': CoupledSpringMass,
  'torsional-pendulum': TorsionalPendulum,
  'physical-pendulum': PhysicalPendulum,

  // === GENERATED EXPERIMENTS - Physics Waves ===
  'standing-waves-string': StandingWavesString,
  'sound-wave-interference': SoundWaveInterference,
  'doppler-moving-source': DopplerMovingSource,
  'doppler-moving-observer': DopplerMovingObserver,
  'beat-frequency': BeatFrequency,
  'wave-superposition': WaveSuperposition,

  // === GENERATED EXPERIMENTS - Physics Thermodynamics ===
  'ideal-gas-isothermal': IdealGasIsothermal,
  'ideal-gas-adiabatic': IdealGasAdiabatic,
  'ideal-gas-isobaric': IdealGasIsobaric,
  'ideal-gas-isochoric': IdealGasIsochoric,
  'carnot-cycle': CarnotCycle,
  'heat-conduction-1d': HeatConduction1D,

  // === GENERATED EXPERIMENTS - Physics Optics ===
  'snells-law-refraction': SnellsLawRefraction,
  'total-internal-reflection': TotalInternalReflection,
  'thin-converging-lens': ThinConvergingLens,
  'thin-diverging-lens': ThinDivergingLens,
  'concave-mirror': ConcaveMirror,
  'convex-mirror': ConvexMirror,
  'double-slit-interference': DoubleSlitInterference,
  'single-slit-diffraction': SingleSlitDiffraction,
  'diffraction-grating': DiffractionGrating,
  'malus-law-polarization': MalusLawPolarization,

  // === GENERATED EXPERIMENTS - Physics Electromagnetism ===
  'coulombs-law': CoulombsLaw,
  'electric-field-point-charge': ElectricFieldPointCharge,
  'parallel-plate-capacitor': ParallelPlateCapacitor,
  'rl-circuit-transient': RLCircuitTransient,
  'rlc-series-resonance': RLCSeriesResonance,
  'rlc-parallel-resonance': RLCParallelResonance,
  'magnetic-force-wire': MagneticForceWire,
  'faraday-law': FaradayLaw,
  'ideal-transformer': IdealTransformer,
  'lc-oscillations': LCOscillations,

  // === GENERATED EXPERIMENTS - Physics Modern Physics ===
  'photoelectric-effect': PhotoelectricEffect,
  'compton-scattering': ComptonScattering,
  'blackbody-radiation': BlackbodyRadiation,
  'bohr-model': BohrModel,
  'radioactive-decay': RadioactiveDecay,
  'radioactive-decay-chain': RadioactiveDecayChain,

  // === GENERATED EXPERIMENTS - Chemistry Physical Chemistry ===
  'vapor-pressure-clausius-clapeyron': VaporPressureClausiusClapeyron,
  'raoults-law-ideal-mixture': RaoultsLawIdealMixture,
  'boiling-point-elevation': BoilingPointElevation,
  'freezing-point-depression': FreezingPointDepression,
  'osmotic-pressure': OsmoticPressure,
  'henrys-law': HenrysLaw,
  'phase-diagram-water-like': PhaseDiagramWaterLike,
  'colligative-properties': ColligativeProperties,

  // === GENERATED EXPERIMENTS - Chemistry Electrochemistry ===
  'galvanic-cell-nernst': GalvanicCellNernst,
  'electrolysis-faraday': ElectrolysisFaraday,
  'concentration-cell': ConcentrationCell,
  'ph-electrode-calibration': pHElectrodeCalibration,
  'conductivity-titration': ConductivityTitration,
  'electroplating-rate': ElectroplatingRate,

  // === GENERATED EXPERIMENTS - Chemistry Thermochemistry ===
  'hesss-law-verification': HesssLawVerification,
  'bomb-calorimetry': BombCalorimetry,
  'enthalpy-of-neutralization': EnthalpyOfNeutralization,
  'enthalpy-of-solution': EnthalpyOfSolution,

  // === GENERATED EXPERIMENTS - Biology Genetics ===
  'hardy-weinberg-equilibrium': HardyWeinbergEquilibrium,
  'genetic-drift': GeneticDrift,
  'natural-selection-directional': NaturalSelectionDirectional,
  'natural-selection-stabilizing': NaturalSelectionStabilizing,
  'mutation-rate-effects': MutationRateEffects,
  'gene-flow-between-populations': GeneFlowBetweenPopulations,

  // === GENERATED EXPERIMENTS - Biology Ecology ===
  'competition-lotka-volterra': CompetitionLotkaVolterra,
  'predator-saturation-type-ii': PredatorSaturationTypeII,
  'predator-switching-type-iii': PredatorSwitchingTypeIII,
  'sir-epidemic-model': SIREpidemicModel,
  'age-structured-population': AgeStructuredPopulation,
  'energy-flow-trophic-levels': EnergyFlowTrophicLevels,

  // === GENERATED EXPERIMENTS - Biology Cell Transport ===
  'facilitated-diffusion': FacilitatedDiffusion,
  'active-transport-atp': ActiveTransportATP,
  'sodium-potassium-atpase': SodiumPotassiumATPase,
  'chemiosmosis': Chemiosmosis,
};

/**
 * Create a simulation instance from either a factory function or a class constructor
 */
function createSimulation(creator: SimulationCreator): SimulationEngine {
  // Check if creator is a class constructor (has prototype)
  if ('prototype' in creator && creator.prototype.constructor === creator) {
    // Generated experiment - instantiate with `new`
    return new (creator as ExperimentClassConstructor)();
  }
  // Manual experiment - call factory function
  return (creator as SimulationFactory)();
}

export function ExperimentPage() {
  const { domain, slug } = useParams<{ domain: string; slug: string }>();
  
  // Find experiment config
  const config = ALL_EXPERIMENTS.find(
    e => e.domain === domain && e.slug === slug
  );
  
  if (!config) {
    return (
      <div className={styles.notFound}>
        <h1>Experiment Not Found</h1>
        <p>The experiment "{domain}/{slug}" does not exist.</p>
        <Link to="/experiments">Browse all experiments</Link>
      </div>
    );
  }
  
  return <ExperimentRunner config={config} />;
}

interface ExperimentRunnerProps {
  config: ExperimentConfig;
}

function ExperimentRunner({ config }: ExperimentRunnerProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [parameters, setParameters] = useState<ParameterValues>(() => {
    const initial: ParameterValues = {};
    for (const param of config.parameters) {
      initial[param.id] = param.default;
    }
    return initial;
  });
  const [state, setState] = useState<SimulationState | null>(null);
  const [history, setHistory] = useState<SimulationState[]>([]);
  
  const simulationRef = useRef<SimulationEngine | null>(null);
  const animationRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  
  // Initialize simulation
  useEffect(() => {
    const creator = SIMULATION_CREATORS[config.slug];
    if (creator) {
      const sim = createSimulation(creator);
      sim.initialize(parameters);
      simulationRef.current = sim;
      setState(sim.getState());
      setHistory([sim.getState()]);
    } else {
      console.warn(`No simulation creator found for: ${config.slug}`);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [config.slug]);
  
  // Animation loop
  const animate = useCallback((timestamp: number) => {
    if (!simulationRef.current || !isRunning) return;
    
    const dt = lastTimeRef.current ? (timestamp - lastTimeRef.current) / 1000 : 1/60;
    lastTimeRef.current = timestamp;
    
    // Clamp dt to prevent spiral of death
    const clampedDt = Math.min(dt, 0.05);
    
    const newState = simulationRef.current.update(clampedDt);
    setState(newState);
    
    // Update history (sample every 5 frames to reduce memory)
    setHistory(prev => {
      const newHistory = [...prev, newState];
      if (newHistory.length > 2000) {
        return newHistory.slice(-2000);
      }
      return newHistory;
    });
    
    if (isRunning && newState.flags.isStable) {
      animationRef.current = requestAnimationFrame(animate);
    } else if (!newState.flags.isStable) {
      setIsRunning(false);
    }
  }, [isRunning]);
  
  // Start/stop animation
  useEffect(() => {
    if (isRunning) {
      lastTimeRef.current = 0;
      animationRef.current = requestAnimationFrame(animate);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRunning, animate]);
  
  const handleParameterChange = (id: string, value: number) => {
    setParameters(prev => ({ ...prev, [id]: value }));
    if (simulationRef.current) {
      simulationRef.current.setParameter(id, value);
      setState(simulationRef.current.getState());
      setHistory([simulationRef.current.getState()]);
    }
  };
  
  const handleReset = () => {
    setIsRunning(false);
    if (simulationRef.current) {
      simulationRef.current.initialize(parameters);
      setState(simulationRef.current.getState());
      setHistory([simulationRef.current.getState()]);
    }
  };
  
  const handlePlayPause = () => {
    setIsRunning(prev => !prev);
  };
  
  return (
    <div className={styles.experimentPage}>
      <header className={styles.header}>
        <div>
          <span className={styles.domain} data-domain={config.domain}>
            {config.domain}
          </span>
          <h1 className={styles.title}>{config.name}</h1>
          <p className={styles.description}>{config.description}</p>
        </div>
        <div className={styles.controls}>
          <button
            className={`${styles.button} ${styles.primary}`}
            onClick={handlePlayPause}
          >
            {isRunning ? '⏸ Pause' : '▶ Run'}
          </button>
          <button
            className={styles.button}
            onClick={handleReset}
          >
            ↺ Reset
          </button>
        </div>
      </header>
      
      {state && !state.flags.isStable && (
        <div className={styles.warning}>
          <strong>⚠ Simulation Failure:</strong> {state.flags.failureMessage}
        </div>
      )}
      
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <ParameterPanel
            parameters={config.parameters}
            values={parameters}
            onChange={handleParameterChange}
            disabled={isRunning}
          />
        </aside>
        
        <main className={styles.main}>
          <div className={styles.visualizationGrid}>
            <section className={styles.panel}>
              <h3 className={styles.panelTitle}>Simulation</h3>
              <SimulationCanvas
                config={config}
                state={state}
                parameters={parameters}
              />
            </section>
            
            <section className={styles.panel}>
              <h3 className={styles.panelTitle}>Graphs</h3>
              <GraphPanel
                config={config}
                history={history}
              />
            </section>
          </div>
          
          <section className={styles.panel}>
            <h3 className={styles.panelTitle}>Governing Equations</h3>
            <EquationDisplay
              equations={config.equations}
              parameters={parameters}
              state={state?.variables ?? {}}
            />
          </section>
          
          {state && (
            <section className={styles.panel}>
              <h3 className={styles.panelTitle}>Current State</h3>
              <div className={styles.stateGrid}>
                <StateValue label="Time" value={state.time.toFixed(3)} unit="s" />
                {Object.entries(state.variables).map(([key, value]) => (
                  <StateValue key={key} label={key} value={value.toFixed(4)} />
                ))}
                {state.energy && (
                  <>
                    <StateValue label="Kinetic Energy" value={state.energy.kinetic.toFixed(4)} unit="J" />
                    <StateValue label="Potential Energy" value={state.energy.potential.toFixed(4)} unit="J" />
                    <StateValue label="Total Energy" value={state.energy.total.toFixed(4)} unit="J" />
                  </>
                )}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

function StateValue({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className={styles.stateValue}>
      <span className={styles.stateLabel}>{label}</span>
      <span className={styles.stateNumber}>
        {value}
        {unit && <span className={styles.stateUnit}> {unit}</span>}
      </span>
    </div>
  );
}
